import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * The search input (`searchInput`) is never part of the fetch-and-morph cycle below —
 * only `pillsResults`, `productsResults`, `extraResults` and `statusRegion` are replaced
 * as the shopper types. Keeping the input outside that morphed region means it's never
 * recreated or reconciled against server-rendered markup, so it can't lose focus, cursor
 * position, or its typed value the way it did when it lived inside the morphed content.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} pillsResults - The search-suggestion pills fragment.
 * @property {HTMLElement} productsResults - The suggested/matching products fragment.
 * @property {HTMLElement} extraResults - The collection/page/article carousels (or no-results message) fragment.
 * @property {HTMLElement} statusRegion - The visually-hidden live region announcing result counts.
 * @property {HTMLElement} scrollBody - The scrollable container all four fragments live in.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = [
    'searchInput',
    'resetButton',
    'pillsResults',
    'productsResults',
    'extraResults',
    'statusRegion',
    'scrollBody',
  ];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * The default (no search performed) markup for each fragment, snapshotted on connect so
   * resetting the search can restore it instantly without a network round trip.
   * @type {{ pills: string, products: string, extra: string, status: string } | null}
   */
  #defaultSnapshot = null;

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  /**
   * Get the dialog component.
   * @returns {import('@theme/dialog').DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { pillsResults, productsResults, extraResults, statusRegion } = this.refs;
    this.#defaultSnapshot = {
      pills: pillsResults.innerHTML,
      products: productsResults.innerHTML,
      extra: extraResults.innerHTML,
      status: statusRegion.innerHTML,
    };

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    const { dialog } = this;
    const { signal } = this.#controller;

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    // Pills and product cards carry ref="resultsItems[]" directly (see
    // predictive-search-results.liquid). Collection/page/article carousel cards
    // (predictive-search-resource-carousel.liquid, reused as-is) don't — they use
    // the older .predictive-search-results__card convention instead.
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')).filter(
        (item) => item instanceof HTMLElement
      )
    );
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items.length) return;

    let activeItem = null;

    items.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of items.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    const totalItems = this.#allResultsItems.length;
    if (!totalItems || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey
          ? currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1
          : currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.extraResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets the scroll position of the results container
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.scrollBody.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and morph each fragment into place.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();
    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        this.#morphFragments(resultsMarkup);
        this.#resetScrollPositions();

        deferredPromise.resolve({ totalCount: this.#allResultsItems.length });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Parses a fetched results response and morphs each of its fragments into the matching
   * live ref, identified by their shared data-predictive-search-* attribute.
   * @param {string} markup
   */
  #morphFragments(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const { statusRegion, pillsResults, productsResults, extraResults } = this.refs;

    /** @type {[HTMLElement | undefined, string][]} */
    const pairs = [
      [statusRegion, 'data-predictive-search-status'],
      [pillsResults, 'data-predictive-search-pills'],
      [productsResults, 'data-predictive-search-products'],
      [extraResults, 'data-predictive-search-extra'],
    ];

    for (const [target, attribute] of pairs) {
      if (!target) continue;
      const source = doc.querySelector(`[${attribute}]`);
      if (source) morph(target, source);
    }
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    this.#currentIndex = -1;
    this.refs.searchInput.value = '';
    this.#hideResetButton();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }

    const { pillsResults, productsResults, extraResults, statusRegion } = this.refs;
    if (this.#defaultSnapshot) {
      pillsResults.innerHTML = this.#defaultSnapshot.pills;
      productsResults.innerHTML = this.#defaultSnapshot.products;
      extraResults.innerHTML = this.#defaultSnapshot.extra;
      statusRegion.innerHTML = this.#defaultSnapshot.status;
    }

    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
